import { Box, ActionIcon, Text, Tooltip } from '@mantine/core';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTrashCan, faArrowsSplitUpAndLeft, faArrowsCross } from '@fortawesome/pro-regular-svg-icons';
import { useTranslation } from 'react-i18next';
import { useAppDispatch } from '@vision/ui/hooks';
import { removeConditionGroup, updateConditionGroupOperator } from '@vision/ui/store/states/survey-logic-builder-state';
import { SurveyLogicConditionGroup, SurveyLogicOperator, SurveyLogicElement } from '@vision/ui/interfaces';
import { ConditionBuilder } from '../ConditionBuilder';
import classes from './ConditionGroupBuilder.module.css';
import { Fragment, memo, useCallback } from 'react';

interface ConditionGroupBuilderProps {
  ruleId: string;
  group: SurveyLogicConditionGroup;
  groupIndex: number;
  elements: SurveyLogicElement[];
  activeLanguageCode: string;
  canDelete: boolean;
}

export const ConditionGroupBuilder = memo(function ConditionGroupBuilder({
  ruleId,
  group,
  groupIndex,
  elements,
  activeLanguageCode,
  canDelete,
}: ConditionGroupBuilderProps) {
  const { t } = useTranslation('page-survey-logic-builder');
  const { t: tCommon } = useTranslation();
  const dispatch = useAppDispatch();

  const handleOperatorToggle = useCallback(() => {
    const currentOperator = group.operator || 'and';
    const newOperator: SurveyLogicOperator = currentOperator === 'and' ? 'or' : 'and';
    dispatch(
      updateConditionGroupOperator({
        ruleId,
        groupId: group.id,
        operator: newOperator,
      }),
    );
  }, [dispatch, ruleId, group.id, group.operator]);

  const handleDeleteGroup = useCallback(() => {
    dispatch(removeConditionGroup({ ruleId, groupId: group.id }));
  }, [dispatch, ruleId, group.id]);

  const conditions = group.conditions || [];
  const operator = group.operator || 'and';
  const totalConditions = conditions.length;

  return (
    <Box className={classes.container} data-testid={`section-condition-group-${groupIndex}`}>
      <Box className={classes.header}>
        <Text className={classes.groupTitle}>
          {t('conditionGroup.title')} {groupIndex + 1}
        </Text>
        {canDelete && (
          <Tooltip label={tCommon('delete')}>
            <ActionIcon
              className={classes.deleteGroupButton}
              onClick={handleDeleteGroup}
              data-testid={`button-delete-group-${groupIndex}`}
            >
              <FontAwesomeIcon icon={faTrashCan} />
            </ActionIcon>
          </Tooltip>
        )}
      </Box>

      <Box className={classes.conditionsLayout}>
        {totalConditions > 1 && (
          <Box
            className={classes.operatorBox}
            style={{ '--condition-count': String(totalConditions) } as React.CSSProperties}
          >
            {totalConditions > 2 &&
              Array.from({ length: totalConditions - 2 }).map((_, idx) => (
                <Box
                  key={idx}
                  className={classes.operatorBoxSeparator}
                  style={{ '--separator-index': String(idx) } as React.CSSProperties}
                />
              ))}
            <Box className={classes.operatorBoxBottom} />
            {Array.from({ length: totalConditions - 1 }).map((_, idx) => (
              <Tooltip key={idx} label={t('conditionBuilder.toggleConditionOperator')} openDelay={300}>
                <Box
                  className={classes.operatorBoxButton}
                  style={{ '--operator-index': String(idx) } as React.CSSProperties}
                  onClick={handleOperatorToggle}
                >
                  <FontAwesomeIcon
                    icon={operator === 'and' ? faArrowsSplitUpAndLeft : faArrowsCross}
                    className={operator === 'and' ? classes.operatorBoxIcon : classes.operatorBoxIconOr}
                  />
                  <Text className={classes.operatorBoxText}>
                    {operator === 'and' ? t('operator.and') : t('operator.or')}
                  </Text>
                </Box>
              </Tooltip>
            ))}
          </Box>
        )}

        <Box className={classes.conditionsContainer}>
          {conditions.map((condition, conditionIndex) => (
            <Fragment key={condition.id}>
              <ConditionBuilder
                ruleId={ruleId}
                groupId={group.id}
                condition={condition}
                conditionIndex={conditionIndex}
                elements={elements}
                activeLanguageCode={activeLanguageCode}
                isLast={conditionIndex === conditions.length - 1}
              />
            </Fragment>
          ))}
        </Box>
      </Box>
    </Box>
  );
});
